import React, { useState, useEffect, useMemo, useCallback } from "react";
import Button from "../Button/Button";
import { ArrowRight, X, ChevronDown, BarChart3, Maximize2, RotateCcw } from "lucide-react";
import { BsFillPieChartFill } from "react-icons/bs";
import { RiBarChart2Fill, RiTableView } from "react-icons/ri";
import { PiChartBarHorizontalFill, PiChartDonutFill } from "react-icons/pi";
import { FaStar } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import inputTypes from "../../assets/inputTypes";
import { isIconValue, iconNameFromValue, getPictureChoiceIcon } from "../../assets/pictureChoiceIcons";

// Picture-choice options can be a real image URL OR an icon token like
// "icon:FileText". Rendering a token straight into <img src> triggers
// ERR_UNKNOWN_URL_SCHEME / CORS errors, so resolve icons to their component.
const OptionThumbnail = ({ src, alt, sizeClass = "w-5 h-5" }) => {
  if (!src) return null;
  if (isIconValue(src)) {
    const Icon = getPictureChoiceIcon(iconNameFromValue(src));
    return (
      <span className={`${sizeClass} flex items-center justify-center rounded border border-gray-300 text-gray-600 flex-shrink-0`}>
        {Icon ? <Icon strokeWidth={1.5} className="w-3/4 h-3/4" /> : null}
      </span>
    );
  }
  return (
    <img src={src} alt={alt} className={`${sizeClass} object-cover rounded border border-gray-300 flex-shrink-0`} />
  );
};

const NON_CHARTABLE_TYPES = new Set([
  "short text", "long text", "email", "date", "contact info", "numeric", "url", "signature", "phone number", "file upload", "matrix", "ranking", "address", "legal", "video audio"
]);

const MAX_LOCALSTORAGE_KEYS = 50;

const SurveyAnalyticsInsight = ({ surveyId, survey }) => {
  const navigate = useNavigate();
  const { currentSubscription } = useSelector((state) => state.app);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [isChartDetailOpen, setIsChartDetailOpen] = useState(false);
  const [chartSettings, setChartSettings] = useState({});
  const [isChartTypeOpen, setIsChartTypeOpen] = useState(false);

  useEffect(() => {
    if (surveyId) {
      const savedSettings = localStorage.getItem(`chartSettings_${surveyId}`);
      if (savedSettings) {
        try {
          setChartSettings(JSON.parse(savedSettings));
        } catch (error) {
          console.error('Error loading chart settings:', error);
        }
      }
    }
  }, [surveyId]);

  useEffect(() => {
    try {
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('chartSettings_')) keys.push(key);
      }
      if (keys.length > MAX_LOCALSTORAGE_KEYS) {
        keys.slice(0, keys.length - MAX_LOCALSTORAGE_KEYS).forEach(k => localStorage.removeItem(k));
      }
    } catch {}
  }, []);

  const chartTypeOptions = [
    { value: 'column', label: 'Column', icon: RiBarChart2Fill },
    { value: 'bar', label: 'Bar', icon: PiChartBarHorizontalFill },
    { value: 'pie', label: 'Pie', icon: BsFillPieChartFill },
    { value: 'donut', label: 'Donut', icon: PiChartDonutFill },
    { value: 'table', label: 'Table', icon: RiTableView }
  ];

  const colorPalette = [
    { name: "Blue", primary: "#3B82F6", secondary: "#60A5FA" },
    { name: "Orange", primary: "#F97316", secondary: "#FB923C" },
    { name: "Purple", primary: "#8B5CF6", secondary: "#A78BFA" },
    { name: "Teal", primary: "#14B8A6", secondary: "#5EEAD4" },
    { name: "Green", primary: "#10B981", secondary: "#6EE7B7" },
    { name: "Red", primary: "#EF4444", secondary: "#F87171" },
    { name: "Pink", primary: "#EC4899", secondary: "#F472B6" },
    { name: "Yellow", primary: "#F59E0B", secondary: "#FBBF24" }
  ];

  const getDefaultSettings = (questionType) => ({
    chartType: questionType === "star rating" ? "column" : questionType === "nps" ? "nps" : "bar",
    valueType: "absolute",
    sorting: "descending",
    colorIndex: 0,
    showValues: true
  });

  const getCurrentSettings = (questionId) => {
    if (chartSettings[questionId]) {
      return chartSettings[questionId];
    }
    const question = survey?.questions?.find(q => q.id === questionId);
    return getDefaultSettings(question?.type || "multiple choice");
  };

  const updateChartSetting = (questionId, key, value) => {
    const newSettings = {
      ...chartSettings,
      [questionId]: {
        ...getCurrentSettings(questionId),
        [key]: value
      }
    };
    setChartSettings(newSettings);
    if (surveyId) {
      localStorage.setItem(`chartSettings_${surveyId}`, JSON.stringify(newSettings));
    }
  };

  const resetChartSettings = (questionId) => {
    const question = survey?.questions?.find(q => q.id === questionId);
    const defaults = getDefaultSettings(question?.type || "multiple choice");
    const newSettings = { ...chartSettings, [questionId]: defaults };
    setChartSettings(newSettings);
    if (surveyId) {
      localStorage.setItem(`chartSettings_${surveyId}`, JSON.stringify(newSettings));
    }
  };

  const displayableQuestions = useMemo(() => {
    if (!survey?.questions) return [];
    return survey.questions.filter(q => q.type !== "statement");
  }, [survey?.questions]);

  const chartableQuestions = useMemo(() => {
    return displayableQuestions.filter(q => !NON_CHARTABLE_TYPES.has(q.type));
  }, [displayableQuestions]);

  const textQuestions = useMemo(() => {
    return displayableQuestions.filter(q => NON_CHARTABLE_TYPES.has(q.type));
  }, [displayableQuestions]);

  const duplicateTitleSet = useMemo(() => {
    const titleCount = {};
    displayableQuestions.forEach(q => {
      titleCount[q.text] = (titleCount[q.text] || 0) + 1;
    });
    return new Set(Object.keys(titleCount).filter(t => titleCount[t] > 1));
  }, [displayableQuestions]);

  const openChartDetail = (question, index) => {
    setSelectedQuestion({ ...question, questionNumber: index + 1 });
    setIsChartDetailOpen(true);
  };

  const closeChartDetail = useCallback(() => {
    setIsChartDetailOpen(false);
    setSelectedQuestion(null);
    setIsChartTypeOpen(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isChartTypeOpen && !event.target.closest('.chart-type-dropdown')) {
        setIsChartTypeOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isChartTypeOpen]);

  const navigateQuestion = useCallback((direction) => {
    if (!selectedQuestion || !chartableQuestions.length) return;
    const currentIndex = chartableQuestions.findIndex(q => q.id === selectedQuestion.id);
    if (currentIndex === -1) return;

    let newIndex;
    if (direction === 'previous') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : chartableQuestions.length - 1;
    } else {
      newIndex = currentIndex < chartableQuestions.length - 1 ? currentIndex + 1 : 0;
    }

    const newQuestion = chartableQuestions[newIndex];
    setSelectedQuestion({ ...newQuestion, questionNumber: newIndex + 1 });
  }, [selectedQuestion, chartableQuestions]);

  useEffect(() => {
    if (!isChartDetailOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeChartDetail();
      if (e.key === 'ArrowLeft') navigateQuestion('previous');
      if (e.key === 'ArrowRight') navigateQuestion('next');
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isChartDetailOpen, closeChartDetail, navigateQuestion]);

  useEffect(() => {
    if (isChartDetailOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isChartDetailOpen]);

  const processDataWithSettings = (question, settings) => {
    let data = getQuestionData(question);

    if (settings.sorting === 'ascending') {
      data = [...data].sort((a, b) => a.value - b.value);
    } else if (settings.sorting === 'descending') {
      data = [...data].sort((a, b) => b.value - a.value);
    }

    if (settings.valueType === 'percentage') {
      const total = data.reduce((sum, item) => sum + item.value, 0);
      data = data.map(item => ({
        ...item,
        displayValue: total > 0 ? `${((item.value / total) * 100).toFixed(1)}%` : '0%',
        percentage: total > 0 ? (item.value / total) * 100 : 0
      }));
    } else {
      data = data.map(item => ({
        ...item,
        displayValue: item.value.toString(),
        percentage: item.percentage
      }));
    }

    return data;
  };

  const getQuestionData = (question) => {
    if (!question || !survey?.responses) return [];

    const responseCounts = {};
    let totalAnswered = 0;

    survey.responses.forEach((response) => {
      const answer = response.answers?.[question.id];
      if (answer === undefined || answer === null) return;

      totalAnswered++;

      if (Array.isArray(answer)) {
        answer.forEach(item => {
          const key = String(item);
          responseCounts[key] = (responseCounts[key] || 0) + 1;
        });
      } else if (question.type === "picture choice" && typeof answer === 'object' && answer.img) {
        const key = answer.label || answer.img;
        responseCounts[key] = (responseCounts[key] || 0) + 1;
      } else {
        const key = String(answer);
        responseCounts[key] = (responseCounts[key] || 0) + 1;
      }
    });

    return Object.entries(responseCounts)
      .map(([label, value]) => ({
        label,
        value,
        percentage: totalAnswered > 0 ? parseFloat(((value / totalAnswered) * 100).toFixed(1)) : 0,
      }));
  };

  const getStarRatingData = (question) => {
    const starCounts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    let totalStars = 0;
    let responseCount = 0;

    if (survey?.responses) {
      survey.responses.forEach((response) => {
        const answer = response.answers?.[question.id];
        if (answer) {
          const rating = parseInt(answer);
          if (rating >= 1 && rating <= 5) {
            starCounts[rating]++;
            totalStars += rating;
            responseCount++;
          }
        }
      });
    }

    const average = responseCount > 0 ? (totalStars / responseCount).toFixed(1) : 0;

    const chartData = Object.entries(starCounts).map(([rating, count]) => ({
      rating: rating,
      count: count,
      percentage: responseCount > 0 ? ((count / responseCount) * 100).toFixed(0) : 0,
    }));

    return { chartData, average, responseCount };
  };

  const getNpsData = (question) => {
    const segments = { detractors: 0, passives: 0, promoters: 0 };
    let totalResponses = 0;

    if (survey?.responses) {
      survey.responses.forEach((response) => {
        const answer = response.answers?.[question.id];
        if (answer === undefined || answer === null) return;
        const score = parseInt(answer);
        if (isNaN(score) || score < 0 || score > 10) return;
        totalResponses++;
        if (score <= 6) segments.detractors++;
        else if (score <= 8) segments.passives++;
        else segments.promoters++;
      });
    }

    const pctDetractors = totalResponses > 0 ? (segments.detractors / totalResponses) * 100 : 0;
    const pctPassives = totalResponses > 0 ? (segments.passives / totalResponses) * 100 : 0;
    const pctPromoters = totalResponses > 0 ? (segments.promoters / totalResponses) * 100 : 0;
    const npsScore = Math.round(pctPromoters - pctDetractors);

    return { segments, totalResponses, pctDetractors, pctPassives, pctPromoters, npsScore };
  };

  const getImageForLabel = (question, label) => {
    if (question.type === "picture choice" && question.options) {
      const option = question.options.find(opt => opt.label === label || opt.img === label);
      return option?.img;
    }
    return null;
  };

  const getAnsweredCount = (question) => {
    if (!survey?.responses) return 0;
    return survey.responses.filter(
      r => r.answers && r.answers[question.id] !== undefined && r.answers[question.id] !== null
    ).length;
  };

  const getTextResponses = (question) => {
    if (!survey?.responses) return [];
    return survey.responses
      .map(r => {
        const val = r.answers?.[question.id];
        if (val === undefined || val === null || val === '') return null;
        const text = typeof val === 'object' ? JSON.stringify(val) : String(val);
        return { text, date: r.submittedAt || r.createdAt };
      })
      .filter(Boolean);
  };

  const renderChartForMainPage = (question) => {
    const settings = getCurrentSettings(question.id);
    const data = processDataWithSettings(question, settings);
    const selectedColor = colorPalette[settings.colorIndex];

    if (question.type === "star rating") {
      return renderCompactStarRating(question);
    }

    if (question.type === "nps") {
      return renderCompactNps(question);
    }

    switch (settings.chartType) {
      case 'table':
        return renderTableChart(question, data, settings, selectedColor);
      case 'pie':
      case 'donut':
        return renderPieDonutChart(question, data, settings, selectedColor);
      case 'column':
        return renderColumnChart(question, data, settings, selectedColor);
      case 'bar':
      default:
        return renderHorizontalBars(question, data, settings, selectedColor);
    }
  };

  const renderHorizontalBars = (question, data, settings, selectedColor) => {
    if (data.length === 0) {
      return <div className="text-center py-8 text-gray-400 text-sm">No responses yet</div>;
    }

    const barColor = selectedColor?.primary || "#14B8A6";

    return (
      <div className="space-y-2 py-2 max-h-48 overflow-y-auto">
        {data.map((item, index) => {
          const imageUrl = getImageForLabel(question, item.label);
          const pct = parseFloat(item.percentage) || 0;
          const displayValue = settings?.showValues === false ? '' :
            (settings?.valueType === 'percentage' ? `${pct.toFixed(1)}%` : item.value);

          return (
            <div key={index} className="flex items-center gap-3">
              {settings?.showValues !== false && (
                <div className="w-12 text-right text-sm font-medium text-gray-700">
                  {pct.toFixed(1)}%
                </div>
              )}
              <div className="flex-1 bg-gray-100 rounded h-8 relative overflow-hidden">
                <div
                  className="h-full rounded transition-all duration-1000 ease-out absolute top-0 left-0"
                  style={{ width: `${pct}%`, backgroundColor: barColor }}
                />
                <div className="absolute inset-0 flex items-center justify-between px-3">
                  <div className="flex items-center gap-2 flex-1">
                    <OptionThumbnail src={imageUrl} alt={item.label} sizeClass="w-5 h-5" />
                    <span className="text-gray-800 font-medium text-xs truncate flex-1">{item.label}</span>
                  </div>
                  <span className="text-gray-600 text-xs font-medium ml-2">{displayValue}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderColumnChart = (question, data, settings, selectedColor, maxBarHeight = 130) => {
    if (data.length === 0) {
      return <div className="text-center py-8 text-gray-400 text-sm">No responses yet</div>;
    }

    const maxValue = Math.max(...data.map(d => d.value));

    return (
      <div className="flex items-end justify-center gap-2 px-2" style={{ height: `${maxBarHeight + 40}px` }}>
        {data.map((item, index) => {
          const ratio = maxValue > 0 ? item.value / maxValue : 0;
          const heightPx = Math.max(12, Math.round(ratio * maxBarHeight));

          return (
            <div key={index} className="flex flex-col items-center gap-1 flex-1 max-w-16 min-w-10 self-end">
              {settings.showValues && (
                <span className="text-xs font-bold text-gray-600">{item.displayValue}</span>
              )}
              <div
                className="w-full rounded-t-md transition-all duration-700 ease-out shadow-sm"
                style={{ height: `${heightPx}px`, backgroundColor: selectedColor.primary }}
              />
              <span className="text-xs text-gray-600 text-center truncate w-full font-medium" title={item.label}>{item.label}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderTableChart = (question, data, settings, selectedColor) => {
    if (data.length === 0) {
      return <div className="text-center py-8 text-gray-400 text-sm">No responses yet</div>;
    }

    return (
      <div className="max-h-40 overflow-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left p-3 font-semibold text-gray-700">Option</th>
                <th className="text-center p-3 font-semibold text-gray-700">Distribution</th>
                <th className="text-right p-3 font-semibold text-gray-700">
                  {settings.valueType === 'percentage' ? 'Percentage' : 'Count'}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.map((item, index) => {
                const imageUrl = getImageForLabel(question, item.label);
                return (
                  <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <OptionThumbnail src={imageUrl} alt={item.label} sizeClass="w-6 h-6" />
                        <span className="text-gray-800 font-medium truncate">{item.label}</span>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center">
                        <div className="w-12 bg-gray-200 rounded-full h-1.5 relative overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-1000 ease-out"
                            style={{ width: `${item.percentage}%`, backgroundColor: selectedColor.primary }}
                          />
                        </div>
                        <span className="ml-2 text-xs text-gray-600 font-medium min-w-[35px]">
                          {typeof item.percentage === 'number' ? item.percentage.toFixed(1) : item.percentage}%
                        </span>
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      <span className="font-semibold text-gray-800 bg-gray-100 px-2 py-1 rounded text-xs">
                        {settings.showValues ? item.displayValue : ''}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const buildPieSegments = (data, selectedColor) => {
    const totalResponses = data.reduce((sum, item) => sum + item.value, 0);
    if (totalResponses === 0) return { segments: [], totalResponses: 0 };

    let cumulativeAngle = 0;
    const segments = data.map((item, index) => {
      const percentage = totalResponses > 0 ? (item.value / totalResponses) * 100 : 0;
      const angle = (percentage / 100) * 360;
      const startAngle = cumulativeAngle;
      const endAngle = cumulativeAngle + angle;
      cumulativeAngle += angle;

      const colors = [
        selectedColor.primary,
        selectedColor.secondary,
        `${selectedColor.primary}CC`,
        `${selectedColor.primary}99`,
        `${selectedColor.primary}66`,
        `${selectedColor.primary}44`
      ];

      return { ...item, percentage, angle, startAngle, endAngle, color: colors[index % colors.length] };
    });

    return { segments, totalResponses };
  };

  const renderPiePath = (segment, chartType, outerR, innerR, center) => {
    if (segment.percentage === 0) return null;

    if (segment.angle >= 359.99) {
      if (chartType === 'donut') {
        return (
          <g key="full">
            <circle cx={center} cy={center} r={outerR} fill={segment.color} stroke="white" strokeWidth="1" />
            <circle cx={center} cy={center} r={innerR} fill="white" />
          </g>
        );
      }
      return <circle key="full" cx={center} cy={center} r={outerR} fill={segment.color} stroke="white" strokeWidth="1" />;
    }

    const startAngleRad = (segment.startAngle * Math.PI) / 180;
    const endAngleRad = (segment.endAngle * Math.PI) / 180;
    const x1 = center + outerR * Math.cos(startAngleRad);
    const y1 = center + outerR * Math.sin(startAngleRad);
    const x2 = center + outerR * Math.cos(endAngleRad);
    const y2 = center + outerR * Math.sin(endAngleRad);
    const largeArcFlag = segment.angle > 180 ? 1 : 0;

    let pathData;
    if (chartType === 'donut') {
      const ix1 = center + innerR * Math.cos(startAngleRad);
      const iy1 = center + innerR * Math.sin(startAngleRad);
      const ix2 = center + innerR * Math.cos(endAngleRad);
      const iy2 = center + innerR * Math.sin(endAngleRad);
      pathData = `M ${x1} ${y1} A ${outerR} ${outerR} 0 ${largeArcFlag} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${innerR} ${innerR} 0 ${largeArcFlag} 0 ${ix1} ${iy1} Z`;
    } else {
      pathData = `M ${center} ${center} L ${x1} ${y1} A ${outerR} ${outerR} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
    }

    return <path key={segment.startAngle} d={pathData} fill={segment.color} stroke="white" strokeWidth="1" />;
  };

  const renderPieDonutChart = (question, data, settings, selectedColor) => {
    const { segments, totalResponses } = buildPieSegments(data, selectedColor);
    if (totalResponses === 0) {
      return <div className="text-center py-8 text-gray-400 text-sm">No responses yet</div>;
    }

    const outerR = settings.chartType === 'donut' ? 80 : 90;
    const innerR = settings.chartType === 'donut' ? 40 : 0;

    return (
      <div className="flex items-center justify-center gap-4 h-auto px-4">
        <div className="relative flex-shrink-0">
          <svg width="200" height="200" className="transform -rotate-90">
            {segments.map((segment) => renderPiePath(segment, settings.chartType, outerR, innerR, 100))}
          </svg>
          {settings.chartType === 'donut' && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="text-lg font-bold text-gray-800">{totalResponses}</div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Total</div>
              </div>
            </div>
          )}
        </div>
        <div className="space-y-1 flex-1 max-w-32 overflow-y-auto max-h-44">
          {segments.map((segment, index) => (
            <div key={index} className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: segment.color }} />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-gray-800 truncate font-medium" title={`${segment.label} (${segment.value})`}>
                  {segment.label} <span className="font-bold">({segment.value})</span>
                </div>
                {settings.showValues && settings.valueType === 'percentage' && (
                  <div className="text-xs text-gray-500">{segment.percentage.toFixed(1)}%</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCompactStarRating = (question) => {
    const { average, responseCount } = getStarRatingData(question);

    return (
      <div className="flex items-center justify-center gap-4 py-6">
        <div className="relative flex items-center justify-center w-24 h-24">
          <FaStar
            className="w-full h-full"
            style={{ color: '#f59e0b', filter: 'drop-shadow(0 4px 8px rgba(245, 158, 11, 0.3))' }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-xl font-bold text-white drop-shadow-lg">{average}</div>
          </div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-800">{average}<span className="text-sm font-normal text-gray-400">/5</span></div>
          <div className="text-xs text-gray-500 mt-1">{responseCount} {responseCount === 1 ? 'rating' : 'ratings'}</div>
        </div>
      </div>
    );
  };

  const renderStarRating = (question, selectedColor = null) => {
    const { chartData, average, responseCount } = getStarRatingData(question);

    const renderStars = (rating) => {
      return Array.from({ length: 5 }, (_, i) => (
        <FaStar key={i} className={`text-lg ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`} />
      ));
    };

    return (
      <div className="flex items-center gap-6 pb-4">
        <div className="flex-1 space-y-3">
          {[...chartData].reverse().map((item) => (
            <div key={item.rating} className="flex items-center gap-4">
              <div className="flex gap-1">{renderStars(item.rating)}</div>
              <div className="text-sm font-medium text-gray-700 min-w-[40px]">{item.percentage}%</div>
              <div className="text-sm text-gray-500 min-w-[40px]">{item.count}</div>
            </div>
          ))}
        </div>
        <div className="flex flex-col items-center justify-center min-w-[140px]">
          <div className="relative flex items-center justify-center w-32 h-32">
            <FaStar
              className="w-full h-full"
              style={{ color: '#f59e0b', filter: 'drop-shadow(0 4px 8px rgba(245, 158, 11, 0.3))' }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-2xl font-bold text-white drop-shadow-lg">{average}</div>
            </div>
          </div>
          <div className="text-sm text-gray-600 font-medium mt-3">Average Rating</div>
        </div>
      </div>
    );
  };

  const getNpsScoreColor = (score) => {
    if (score >= 50) return "#22C55E";
    if (score >= 0) return "#F59E0B";
    return "#EF4444";
  };

  const getNpsScoreLabel = (score) => {
    if (score >= 70) return "Excellent";
    if (score >= 50) return "Great";
    if (score >= 30) return "Good";
    if (score >= 0) return "Needs improvement";
    return "Critical";
  };

  const renderCompactNps = (question) => {
    const { npsScore, totalResponses: npsTotal, pctDetractors, pctPassives, pctPromoters } = getNpsData(question);
    if (npsTotal === 0) {
      return <div className="text-center py-8 text-gray-400 text-sm">No responses yet</div>;
    }
    const scoreColor = getNpsScoreColor(npsScore);
    return (
      <div className="flex flex-col items-center gap-3 py-4">
        <div className="flex items-center gap-4">
          <div className="flex flex-col items-center">
            <div className="text-4xl font-bold" style={{ color: scoreColor }}>{npsScore}</div>
            <div className="text-xs text-gray-500 mt-1">NPS Score</div>
          </div>
          <div className="h-12 w-px bg-gray-200" />
          <div className="text-xs text-gray-500 space-y-1">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-red-400 inline-block" />
              Detractors {pctDetractors.toFixed(0)}%
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-yellow-400 inline-block" />
              Passives {pctPassives.toFixed(0)}%
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-400 inline-block" />
              Promoters {pctPromoters.toFixed(0)}%
            </div>
          </div>
        </div>
        <div className="w-full h-3 rounded-full overflow-hidden flex bg-gray-100">
          {pctDetractors > 0 && <div className="h-full bg-red-400" style={{ width: `${pctDetractors}%` }} />}
          {pctPassives > 0 && <div className="h-full bg-yellow-400" style={{ width: `${pctPassives}%` }} />}
          {pctPromoters > 0 && <div className="h-full bg-green-400" style={{ width: `${pctPromoters}%` }} />}
        </div>
        <div className="text-xs text-gray-400">{npsTotal} {npsTotal === 1 ? 'response' : 'responses'}</div>
      </div>
    );
  };

  const renderNpsDetail = (question) => {
    const { npsScore, segments, totalResponses: npsTotal, pctDetractors, pctPassives, pctPromoters } = getNpsData(question);
    if (npsTotal === 0) {
      return <div className="text-center py-12 text-gray-400">No responses yet</div>;
    }
    const scoreColor = getNpsScoreColor(npsScore);
    const label = getNpsScoreLabel(npsScore);

    const segmentData = [
      { name: "Detractors", range: "0–6", count: segments.detractors, pct: pctDetractors, color: "#F87171", bg: "#FEF2F2" },
      { name: "Passives", range: "7–8", count: segments.passives, pct: pctPassives, color: "#FBBF24", bg: "#FFFBEB" },
      { name: "Promoters", range: "9–10", count: segments.promoters, pct: pctPromoters, color: "#34D399", bg: "#F0FDF4" },
    ];

    return (
      <div className="flex flex-col gap-6 py-4 w-full">
        <div className="flex items-center justify-center gap-6">
          <div className="flex flex-col items-center">
            <div className="text-6xl font-bold" style={{ color: scoreColor }}>{npsScore}</div>
            <div className="text-sm font-medium mt-1" style={{ color: scoreColor }}>{label}</div>
            <div className="text-xs text-gray-400 mt-0.5">NPS Score</div>
          </div>
        </div>

        <div className="w-full h-5 rounded-full overflow-hidden flex bg-gray-100">
          {pctDetractors > 0 && <div className="h-full bg-red-400 transition-all duration-700" style={{ width: `${pctDetractors}%` }} />}
          {pctPassives > 0 && <div className="h-full bg-yellow-400 transition-all duration-700" style={{ width: `${pctPassives}%` }} />}
          {pctPromoters > 0 && <div className="h-full bg-green-400 transition-all duration-700" style={{ width: `${pctPromoters}%` }} />}
        </div>

        <div className="grid grid-cols-3 gap-3">
          {segmentData.map((seg) => (
            <div key={seg.name} className="rounded-xl p-4 text-center" style={{ backgroundColor: seg.bg }}>
              <div className="text-2xl font-bold" style={{ color: seg.color }}>{seg.pct.toFixed(0)}%</div>
              <div className="text-sm font-semibold text-gray-700 mt-1">{seg.name}</div>
              <div className="text-xs text-gray-400">{seg.range} · {seg.count}</div>
            </div>
          ))}
        </div>

        <div className="text-center text-xs text-gray-400">
          Based on {npsTotal} {npsTotal === 1 ? 'response' : 'responses'} · Formula: % Promoters − % Detractors
        </div>
      </div>
    );
  };

  const totalResponses = survey?.responses?.length || 0;

  if (currentSubscription && currentSubscription.plan?.type === "free") {
    return (
      <div className="min-h-[calc(100vh-3.5rem)] bg-white relative">
        <div
          id="overlay"
          className="absolute top-0 left-0 bottom-0 right-0 backdrop-blur-sm bg-white/30 flex justify-center"
          style={{ zIndex: 12 }}
        >
          <div className="text-center mt-40">
            <h2 className="text-3xl mb-4">Access powerful insights</h2>
            <p className="text-sm mb-6 max-w-[400px]">
              Available on paid plans to help you optimize every response you collect.
            </p>
            <Button
              size="sm"
              endIcon={<ArrowRight size={16} />}
              className="text-xs text-app-black px-6 rounded-xl bg-[#FEFD7D] hover:bg-[#ffff93]"
              onClick={() => navigate('/profile?tab=billing')}
            >
              Make It Smarter
            </Button>
          </div>
        </div>
        <div className="w-full">
          <img className="w-full" src="/images/insightsPlaceholder.svg" alt="placeholder" />
        </div>
      </div>
    );
  }

  if (!survey?.responses || survey.responses.length === 0) {
    return (
      <div className="min-h-[calc(100vh-3.5rem)] bg-white flex items-center justify-center -mt-[calc(var(--navbar-height,64px)+3.5rem)]">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No responses yet</h3>
          <p className="text-sm text-gray-500">Share your survey to start collecting responses and see analytics here.</p>
        </div>
      </div>
    );
  }

  if (displayableQuestions.length === 0) {
    return (
      <div className="min-h-[calc(100vh-3.5rem)] bg-white flex items-center justify-center -mt-[calc(var(--navbar-height,64px)+3.5rem)]">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No analyzable questions</h3>
          <p className="text-sm text-gray-500">This survey only contains statement-type questions which don't collect responses.</p>
        </div>
      </div>
    );
  }

  const getChartTypeIcon = (chartType) => {
    const opt = chartTypeOptions.find(o => o.value === chartType);
    return opt ? opt.icon : null;
  };

  return (
    <div className="min-h-[calc(100vh-3.5rem)] bg-white py-8 -mt-[calc(var(--navbar-height,64px)+3.5rem)] relative overflow-hidden">
      <div className="absolute -top-28 left-1/4 w-72 h-72 rounded-full bg-[#EFFBFF] blur-3xl opacity-70 pointer-events-none" />
      <div className="absolute top-32 right-10 w-64 h-64 rounded-full bg-[#9AF2FF]/25 blur-3xl pointer-events-none" />

      <div className="relative flex justify-center">
        <div className="max-w-[1060px] w-full px-4">
          <div className="mb-6">
            <p className="text-sm font-medium text-gray-400">Analyze results</p>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mt-2">
              <div>
                <h2 className="text-2xl font-semibold tracking-[-0.02em] text-gray-950">Analyze results</h2>
                <p className="text-sm text-gray-500 mt-2 max-w-xl">
                  Explore response distribution and answer patterns across your survey questions.
                </p>
              </div>
              <p className="text-sm text-gray-500">
                {totalResponses} {totalResponses === 1 ? 'response' : 'responses'} · {displayableQuestions.length} {displayableQuestions.length === 1 ? 'question' : 'questions'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-line/70 px-5 py-4 shadow-[0_12px_32px_rgba(15,23,42,0.04)]">
              <p className="text-xs font-medium text-gray-400">Total responses</p>
              <p className="text-2xl font-semibold text-gray-950 mt-1">{totalResponses}</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-line/70 px-5 py-4 shadow-[0_12px_32px_rgba(15,23,42,0.04)]">
              <p className="text-xs font-medium text-gray-400">Questions analyzed</p>
              <p className="text-2xl font-semibold text-gray-950 mt-1">{displayableQuestions.length}</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-line/70 px-5 py-4 shadow-[0_12px_32px_rgba(15,23,42,0.04)]">
              <p className="text-xs font-medium text-gray-400">Chart questions</p>
              <p className="text-2xl font-semibold text-gray-950 mt-1">{chartableQuestions.length}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-start">
            {chartableQuestions.map((question, index) => {
              const questionTypeInfo = inputTypes[question.type] || inputTypes["multiple choice"];
              const answeredCount = getAnsweredCount(question);
              const hasDuplicateTitle = duplicateTitleSet.has(question.text);
              const currentChartType = getCurrentSettings(question.id).chartType;
              const ChartIcon = getChartTypeIcon(currentChartType);

              return (
                <div key={question.id} className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 border border-line/70 shadow-[0_14px_40px_rgba(15,23,42,0.06)] hover:shadow-[0_18px_50px_rgba(15,23,42,0.09)] transition-all duration-200 w-full flex flex-col min-h-80">
                  <div className="flex-shrink-0 pb-4 border-b border-gray-100">
                    <div className="flex items-start gap-3">
                      <div
                        className={`flex items-center px-2 justify-between w-12 h-7 rounded-lg shrink-0 ${questionTypeInfo?.bgColor} text-app-black text-sm shadow-sm`}
                      >
                        {questionTypeInfo?.icon && <questionTypeInfo.icon className="w-3 h-3" />}
                        <span className="text-xs">{index + 1}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start gap-2">
                          <h3 className="text-[15px] font-semibold leading-snug text-gray-950 line-clamp-2 flex-1" title={question.text}>{question.text}</h3>
                          {ChartIcon && question.type !== "star rating" && question.type !== "nps" && (
                            <ChartIcon className="w-3.5 h-3.5 text-gray-300 flex-shrink-0 mt-0.5" title={currentChartType} />
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-1.5">
                          <span className="text-xs text-gray-400">{answeredCount} {answeredCount === 1 ? 'response' : 'responses'}</span>
                          {hasDuplicateTitle && (
                            <span className="text-xs text-gray-400">· {questionTypeInfo?.label || question.type}</span>
                          )}
                          {answeredCount > 0 && answeredCount < 3 && (
                            <span className="text-[11px] font-medium text-amber-600 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full">Low sample size</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex-grow overflow-hidden relative mt-4">
                    {renderChartForMainPage(question)}
                  </div>

                  <div className="flex-shrink-0 mt-auto pt-3 border-t border-gray-100 flex items-center justify-between">
                    <button
                      onClick={() => openChartDetail(question, index)}
                      className="inline-flex items-center gap-1.5 text-gray-600 hover:text-gray-950 text-xs font-medium transition-all rounded-lg border border-gray-200 bg-gray-50 hover:bg-white px-2.5 py-1.5"
                    >
                      <Maximize2 className="w-3 h-3" />
                      <span>Expand</span>
                    </button>
                  </div>
                </div>
              );
            })}

            {textQuestions.map((question) => {
              const questionTypeInfo = inputTypes[question.type] || inputTypes["short text"];
              const responses = getTextResponses(question);
              const qIndex = displayableQuestions.findIndex(q => q.id === question.id);

              return (
                <div key={question.id} className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 border border-line/70 shadow-[0_14px_40px_rgba(15,23,42,0.06)] hover:shadow-[0_18px_50px_rgba(15,23,42,0.09)] transition-all duration-200 w-full flex flex-col">
                  <div className="flex items-start gap-3 pb-4 border-b border-gray-100">
                    <div className={`flex items-center px-2 justify-between w-12 h-7 rounded-lg shrink-0 ${questionTypeInfo?.bgColor} text-app-black text-sm shadow-sm`}>
                      {questionTypeInfo?.icon && <questionTypeInfo.icon className="w-3 h-3" />}
                      <span className="text-xs">{qIndex + 1}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-[15px] font-semibold leading-snug text-gray-950 line-clamp-2" title={question.text}>{question.text}</h3>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-xs text-gray-400">{responses.length} {responses.length === 1 ? 'response' : 'responses'}</span>
                      </div>
                    </div>
                  </div>
                  {responses.length > 0 ? (
                    <div className="space-y-2 max-h-[240px] overflow-y-auto mt-4">
                      {responses.map((r, i) => (
                        <div key={i} className="px-3 py-2.5 bg-gray-50/80 border border-gray-100 rounded-xl">
                          <p className="text-sm text-gray-700">{r.text}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-8 text-sm text-gray-400">No responses yet</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {isChartDetailOpen && selectedQuestion && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={closeChartDetail}
        >
          <div
            className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col md:flex-row shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex-1 p-4 md:p-6 overflow-y-auto">
              <div className="mb-4">
                <div className="flex items-center gap-3 mb-3">
                  {(() => {
                    const questionTypeInfo = inputTypes[selectedQuestion.type] || inputTypes["multiple choice"];
                    return (
                      <div
                        className={`flex items-center px-3 justify-between w-16 h-8 rounded-lg ${questionTypeInfo?.bgColor} text-app-black text-base`}
                      >
                        {questionTypeInfo?.icon && <questionTypeInfo.icon className="w-4 h-4" />}
                        <span className="text-sm font-medium">{selectedQuestion.questionNumber}</span>
                      </div>
                    );
                  })()}
                  <h2 className="text-lg md:text-xl font-semibold text-gray-900">{selectedQuestion.text}</h2>
                </div>
              </div>

              <div className="min-h-[16rem] md:min-h-[24rem] bg-gray-50 rounded-lg flex items-center justify-center">
                <div className="w-full h-full p-4">
                  {(() => {
                    if (selectedQuestion.type === "nps") {
                      return renderNpsDetail(selectedQuestion);
                    }

                    const currentSettings = getCurrentSettings(selectedQuestion.id);
                    const processedData = processDataWithSettings(selectedQuestion, currentSettings);
                    const selectedColor = colorPalette[currentSettings.colorIndex];

                    if (currentSettings.chartType === 'table') {
                      return (
                        <div className="w-full h-full overflow-auto">
                          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                                <tr>
                                  <th className="text-left p-4 font-semibold text-gray-700">Option</th>
                                  <th className="text-center p-4 font-semibold text-gray-700 min-w-[160px]">Distribution</th>
                                  <th className="text-right p-4 font-semibold text-gray-700">
                                    {currentSettings.valueType === 'percentage' ? 'Percentage' : 'Count'}
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {processedData.map((item, index) => {
                                  const imageUrl = getImageForLabel(selectedQuestion, item.label);
                                  return (
                                    <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                                      <td className="p-4">
                                        <div className="flex items-center gap-3">
                                          <OptionThumbnail src={imageUrl} alt={item.label} sizeClass="w-8 h-8" />
                                          <span className="text-gray-800 font-medium">{item.label}</span>
                                        </div>
                                      </td>
                                      <td className="p-4 text-center">
                                        <div className="flex items-center justify-center gap-2">
                                          <div className="w-24 bg-gray-200 rounded-full h-2.5 relative overflow-hidden">
                                            <div
                                              className="h-full rounded-full transition-all duration-1000 ease-out"
                                              style={{ width: `${item.percentage}%`, backgroundColor: selectedColor.primary }}
                                            />
                                          </div>
                                          <span className="text-xs text-gray-600 font-medium min-w-[40px]">
                                            {typeof item.percentage === 'number' ? item.percentage.toFixed(1) : item.percentage}%
                                          </span>
                                        </div>
                                      </td>
                                      <td className="p-4 text-right">
                                        <span className="font-semibold text-gray-800 bg-gray-100 px-2 py-1 rounded">
                                          {currentSettings.showValues ? item.displayValue : ''}
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    } else if (currentSettings.chartType === 'pie' || currentSettings.chartType === 'donut') {
                      const { segments, totalResponses: pieTotal } = buildPieSegments(processedData, selectedColor);

                      const svgSize = 240;
                      const center = svgSize / 2;
                      const outerR = currentSettings.chartType === 'donut' ? 100 : 110;
                      const innerR = currentSettings.chartType === 'donut' ? 55 : 0;

                      return (
                        <div className="flex flex-col items-center w-full h-full overflow-auto">
                          <div className="flex-shrink-0 my-auto pt-2">
                            <div className="relative mx-auto" style={{ width: svgSize, height: svgSize }}>
                              <svg width={svgSize} height={svgSize} className="transform -rotate-90">
                                <circle cx={center} cy={center} r={outerR} fill="none" stroke="#F3F4F6" strokeWidth="2" />
                                {segments.map((segment) => renderPiePath(segment, currentSettings.chartType, outerR, innerR, center))}
                              </svg>
                              {currentSettings.chartType === 'donut' && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="text-center">
                                    <div className="text-2xl font-bold text-gray-800">{pieTotal}</div>
                                    <div className="text-xs text-gray-500 uppercase tracking-wide">Total</div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="w-full flex-shrink-0 mt-4 pb-2">
                            <div className="grid grid-cols-2 gap-2">
                              {segments.map((segment, index) => (
                                <div key={index} className="flex items-center gap-2 text-sm bg-white border border-gray-100 rounded-lg px-3 py-2 shadow-sm">
                                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: segment.color }} />
                                  <span className="text-gray-800 font-medium text-xs truncate flex-1" title={segment.label}>{segment.label}</span>
                                  <span className="text-gray-500 text-xs flex-shrink-0">{segment.percentage.toFixed(1)}%</span>
                                  {currentSettings.showValues && (
                                    <span className="font-semibold text-gray-700 text-xs flex-shrink-0">({segment.value})</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    } else {
                      return (
                        <div className="w-full h-full">
                          {currentSettings.chartType === 'bar' ? (
                            <div className="space-y-3 h-full overflow-auto py-4">
                              {processedData.map((item, index) => {
                                const imageUrl = getImageForLabel(selectedQuestion, item.label);
                                const pct = parseFloat(item.percentage) || 0;
                                const isTop = index === 0 && processedData.length > 1 && currentSettings.sorting === 'descending';
                                return (
                                  <div key={index} className="flex items-center gap-3">
                                    {currentSettings.showValues && (
                                      <div className="w-14 text-right text-sm font-medium text-gray-700">
                                        {pct.toFixed(1)}%
                                      </div>
                                    )}
                                    <div className={`flex-1 rounded-md h-10 relative overflow-hidden ${isTop ? 'bg-gray-100 ring-1 ring-gray-200' : 'bg-gray-100'}`}>
                                      <div
                                        className="h-full rounded-md transition-all duration-1000 ease-out absolute top-0 left-0"
                                        style={{ width: `${pct}%`, backgroundColor: selectedColor.primary, opacity: isTop ? 1 : 0.85 }}
                                      />
                                      <div className="absolute inset-0 flex items-center justify-between px-4">
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                          <OptionThumbnail src={imageUrl} alt={item.label} sizeClass="w-5 h-5" />
                                          <span className="text-gray-800 font-medium text-sm truncate">{item.label}</span>
                                        </div>
                                        <span className="text-gray-600 text-sm font-medium ml-2 flex-shrink-0">
                                          {currentSettings.showValues ? (currentSettings.valueType === 'percentage' ? `${pct.toFixed(1)}%` : item.value) : ''}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            selectedQuestion.type === "star rating" ? (
                              renderStarRating(selectedQuestion, selectedColor)
                            ) : (
                              <div className="flex flex-col w-full h-full px-4 pb-4 pt-2">
                                {(() => {
                                  const modalMaxValue = Math.max(...processedData.map(d => d.value));
                                  const gridLines = 4;
                                  const step = modalMaxValue > 0 ? Math.ceil(modalMaxValue / gridLines) : 1;

                                  return (
                                    <>
                                      <div className="flex flex-1 items-end gap-3 relative">
                                        <div className="absolute inset-0 flex flex-col justify-between pointer-events-none pb-8">
                                          {Array.from({ length: gridLines + 1 }).map((_, i) => {
                                            const val = step * (gridLines - i);
                                            return (
                                              <div key={i} className="flex items-center gap-2 w-full">
                                                <span className="text-xs text-gray-400 w-8 text-right flex-shrink-0">{val}</span>
                                                <div className="flex-1 border-b border-gray-200 border-dashed" />
                                              </div>
                                            );
                                          })}
                                        </div>
                                        <div className="flex items-end justify-center gap-3 w-full pl-10 relative z-10">
                                          {processedData.map((item, index) => {
                                            const chartH = 220;
                                            const heightPx = modalMaxValue > 0 ? Math.max(24, (item.value / (step * gridLines)) * chartH) : 24;
                                            return (
                                              <div key={index} className="flex flex-col items-center gap-1 flex-1 max-w-24 min-w-14" title={`${item.label}: ${item.displayValue}`}>
                                                {currentSettings.showValues && (
                                                  <span className="text-xs font-semibold text-gray-600 mb-0.5">{item.displayValue}</span>
                                                )}
                                                <div
                                                  className="w-full rounded-t-md transition-all duration-700 ease-out"
                                                  style={{ height: `${heightPx}px`, backgroundColor: selectedColor.primary }}
                                                />
                                                <span className="text-xs text-gray-600 text-center truncate w-full font-medium mt-1" title={item.label}>{item.label}</span>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    </>
                                  );
                                })()}
                              </div>
                            )
                          )}
                        </div>
                      );
                    }
                  })()}
                </div>
              </div>
            </div>

            <div className="w-full md:w-80 bg-gray-50 p-4 md:p-6 border-t md:border-t-0 md:border-l border-gray-200 overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">Settings</h3>
                <button onClick={closeChartDetail} className="text-gray-400 hover:text-gray-600 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {(() => {
                const currentSettings = getCurrentSettings(selectedQuestion.id);
                return (
                  <>
                    {selectedQuestion.type !== "star rating" && selectedQuestion.type !== "nps" && (
                      <div className="mb-6">
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">CHART</label>
                        <div className="relative chart-type-dropdown">
                          <button
                            type="button"
                            onClick={() => setIsChartTypeOpen(!isChartTypeOpen)}
                            className={`w-full p-3 pl-10 pr-10 border rounded-lg bg-white text-sm text-left flex items-center justify-between transition-all duration-200 ${
                              isChartTypeOpen
                                ? 'border-blue-300 ring-2 ring-blue-100 shadow-sm'
                                : 'border-gray-300 hover:border-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                            }`}
                          >
                            <span className="font-medium text-gray-700">
                              {chartTypeOptions.find(opt => opt.value === currentSettings.chartType)?.label || 'Column'}
                            </span>
                            <ChevronDown className={`w-4 h-4 text-gray-500 transition-all duration-200 ${isChartTypeOpen ? 'rotate-180 text-blue-600' : ''}`} />
                          </button>
                          <div className="absolute left-3 top-3.5 pointer-events-none">
                            {(() => {
                              const selectedOption = chartTypeOptions.find(opt => opt.value === currentSettings.chartType);
                              if (selectedOption) {
                                const IconComponent = selectedOption.icon;
                                return <IconComponent className={`w-4 h-4 transition-colors duration-200 ${isChartTypeOpen ? 'text-blue-600' : 'text-gray-500'}`} />;
                              }
                              return null;
                            })()}
                          </div>
                          {isChartTypeOpen && (
                            <div className="absolute z-20 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
                              {chartTypeOptions.map((option, index) => {
                                const IconComponent = option.icon;
                                const isSelected = currentSettings.chartType === option.value;
                                const isLast = index === chartTypeOptions.length - 1;
                                return (
                                  <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => {
                                      updateChartSetting(selectedQuestion.id, 'chartType', option.value);
                                      setIsChartTypeOpen(false);
                                    }}
                                    className={`w-full text-left px-4 py-3 hover:bg-blue-50 cursor-pointer flex items-center gap-3 transition-all duration-200 border-b ${
                                      isLast ? 'border-b-0' : 'border-gray-100'
                                    } ${isSelected ? 'bg-blue-50 text-blue-700 border-blue-100' : 'text-gray-700 hover:text-blue-600'}`}
                                  >
                                    <IconComponent className={`w-4 h-4 transition-colors duration-200 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />
                                    <span className="text-sm font-medium">{option.label}</span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="mb-6">
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">VALUES</label>
                      <div className="relative">
                        <select
                          value={currentSettings.valueType}
                          onChange={(e) => updateChartSetting(selectedQuestion.id, 'valueType', e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                        >
                          <option value="absolute">Absolute</option>
                          <option value="percentage">Percentage</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-3.5 w-4 h-4 text-gray-400 pointer-events-none" />
                      </div>
                    </div>

                    <div className="mb-6">
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">SORTING</label>
                      <div className="relative">
                        <select
                          value={currentSettings.sorting}
                          onChange={(e) => updateChartSetting(selectedQuestion.id, 'sorting', e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                        >
                          <option value="descending">Descending</option>
                          <option value="ascending">Ascending</option>
                          <option value="original">Original order</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-3.5 w-4 h-4 text-gray-400 pointer-events-none" />
                      </div>
                    </div>

                    <div className="mb-6">
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">COLOR</label>
                      <div className="grid grid-cols-4 gap-2">
                        {colorPalette.map((color, i) => (
                          <button
                            key={i}
                            onClick={() => updateChartSetting(selectedQuestion.id, 'colorIndex', i)}
                            className={`w-8 h-8 rounded-lg transition-all duration-200 ${
                              currentSettings.colorIndex === i
                                ? 'ring-2 ring-offset-2 ring-blue-500 scale-110'
                                : 'hover:scale-105'
                            }`}
                            style={{ backgroundColor: color.primary }}
                            title={color.name}
                          />
                        ))}
                      </div>
                    </div>

                    <div className="mb-6">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">SHOW VALUES</label>
                        <button
                          onClick={() => updateChartSetting(selectedQuestion.id, 'showValues', !currentSettings.showValues)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                            currentSettings.showValues ? 'bg-blue-500' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                              currentSettings.showValues ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>

                    <button
                      onClick={() => resetChartSettings(selectedQuestion.id)}
                      className="flex items-center gap-2 text-xs text-gray-400 hover:text-gray-600 transition-colors mb-4"
                    >
                      <RotateCcw className="w-3 h-3" />
                      <span>Reset to default</span>
                    </button>
                  </>
                );
              })()}

              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <button
                  onClick={() => navigateQuestion('previous')}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-800 text-sm font-medium transition-colors"
                >
                  <span>← Previous</span>
                </button>
                <button
                  onClick={() => navigateQuestion('next')}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-800 text-sm font-medium transition-colors"
                >
                  <span>Next →</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyAnalyticsInsight;
